import { Lightbulb, X, Shuffle } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/utils';

export interface QuickChallengeCardProps {
  question?: string;
  category?: string;
  color?: string;
  onQuestionClick?: () => void;
  onClose?: () => void;
  onShuffle?: () => void;
  setShowQuickChallenge?: (state: {
    open: boolean;
    question: string;
    category?: string;
    color?: string;
  }) => void;
  setCurrentElement?: (element: { type: string } | null) => void;
  className?: string;
}

const QuickChallengeCard = React.forwardRef<
  HTMLDivElement,
  QuickChallengeCardProps
>(
  (
    {
      question = 'Did you know Pi has over 31 trillion digits? What are its first 3 digits?',
      category = 'Quick Challenge',
      color = 'var(--color-underwater-fern-green)',
      onQuestionClick,
      onClose,
      onShuffle,
      setShowQuickChallenge,
      setCurrentElement,
      className,
      ...props
    },
    ref
  ) => {
    const handleClose = React.useCallback(() => {
      // Close the challenge
      setShowQuickChallenge?.({ open: false, question: '' });
      setCurrentElement?.(null);
      onClose?.();
    }, [setShowQuickChallenge, setCurrentElement, onClose]);

    const handleQuestionClick = React.useCallback(() => {
      // Handle question interaction
      onQuestionClick?.();
    }, [onQuestionClick]);

    const handleShuffle = React.useCallback(() => {
      // Handle shuffle
      onShuffle?.();
    }, [onShuffle]);

    return (
      <div
        ref={ref}
        className={cn(
          'rounded-[14px] p-4 w-full',
          'shadow-lg shadow-black/20',
          className
        )}
        style={{
          backgroundColor: color,
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="quick-challenge-title"
        data-node-id="1528:5630"
        {...props}
      >
        {/* Header with lightbulb icon, title, shuffle button, and close button */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Lightbulb
              className="w-5 h-5 text-white"
              fill="currentColor"
              aria-hidden="true"
            />
            <h2
              id="quick-challenge-title"
              className="text-white font-medium text-base"
            >
              {category}
            </h2>
          </div>

          <div className="flex items-center gap-2">
            {/* Shuffle Button */}
            <button
              type="button"
              onClick={handleShuffle}
              className={cn(
                'flex items-center gap-1 px-3 py-1 rounded-full',
                'bg-white/20 hover:bg-white/30',
                'text-white text-sm font-medium',
                'transition-colors duration-200',
                'focus:outline-none focus:ring-2 focus:ring-white/30'
              )}
              aria-label="Shuffle question"
            >
              <Shuffle className="w-4 h-4" />
              <span>Shuffle</span>
            </button>

            {/* Close Button */}
            <button
              type="button"
              onClick={handleClose}
              className={cn(
                'w-6 h-6 rounded-full flex items-center justify-center',
                'text-white/80 hover:text-white hover:bg-white/10',
                'transition-colors duration-200',
                'focus:outline-none focus:ring-2 focus:ring-white/30'
              )}
              aria-label="Close challenge"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Question Text */}
        <button
          type="button"
          onClick={handleQuestionClick}
          className={cn(
            'text-white font-medium text-base leading-relaxed',
            'cursor-pointer hover:text-white/90 transition-colors duration-200',
            'focus:outline-none focus:ring-2 focus:ring-white/30 rounded p-1 -m-1',
            'w-full text-left bg-transparent border-none'
          )}
          aria-label="Click to interact with question"
        >
          {question}
        </button>
      </div>
    );
  }
);

QuickChallengeCard.displayName = 'QuickChallengeCard';

// Memoize the component to prevent unnecessary re-renders
const QuickChallengeCardMemo = React.memo(QuickChallengeCard);

export default QuickChallengeCardMemo;
